// Ground-truth feedback panel — shows pending prompts created by the
// server's uncertainty-gated triggers (see server/src/feedbackPrompts.ts).
//
// Design principles enforced here:
//   1. Uncertainty-gated triggers run server-side; the client only
//      renders what was queued.
//   2. Tiered by friction — binary chips are one-click submits, free
//      text is optional, every card has an explicit Ignore button so
//      the user is never forced to guess.
//   3. Visible payoff — footer reports per-vehicle ground-truth
//      counts so the user sees their participation has weight.
//
// The Ignore button is load-bearing. A wrong "Yes" pollutes the
// training set worse than no answer at all; an Ignored prompt is a
// distinct signal (frequent ignores on a kind tells us the trigger
// is mis-tuned).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api, FeedbackPrompt, FeedbackPromptsResponse, FeedbackPromptKind,
  FeedbackStats, Vehicle,
} from './api';

interface Props {
  /** Currently selected vehicle — used for filtering the stats footer
   *  and for showing the vehicle name on each card. */
  selectedVehicle: Vehicle | null;
  /** When recording is active we still poll, but slowly — the user is
   *  busy. */
  recordingActive: boolean;
  /** Bumped by the parent when a new recording finishes analysis, so
   *  the panel re-fetches immediately (rather than waiting for the
   *  next poll tick). */
  refreshSignal: number;
}

const POLL_IDLE_MS    = 30_000;
const POLL_RECORD_MS  = 90_000;

export function FeedbackPanel({ selectedVehicle, recordingActive, refreshSignal }: Props) {
  const [prompts, setPrompts] = useState<FeedbackPrompt[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get<FeedbackPromptsResponse>('/api/prompts/pending'),
        api.get<FeedbackStats>(
          `/api/prompts/stats${selectedVehicle ? `?vehicle_id=${selectedVehicle.id}` : ''}`,
        ),
      ]);
      setPrompts(p.prompts);
      setStats(s);
      setError(null);
    } catch (e) {
      // Don't spam errors — the panel is opportunistic, not critical.
      setError((e as Error).message);
    }
  }, [selectedVehicle?.id]);

  // Initial + polled refresh.
  useEffect(() => {
    void fetchAll();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(
      fetchAll,
      recordingActive ? POLL_RECORD_MS : POLL_IDLE_MS,
    );
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAll, recordingActive]);

  // Refresh on parent signal (e.g. analysis_result message arrived).
  useEffect(() => {
    if (refreshSignal > 0) void fetchAll();
  }, [refreshSignal, fetchAll]);

  const submit = useCallback(async (id: number, response: object) => {
    setBusyId(id);
    try {
      await api.post(`/api/prompts/${id}/answer`, { response });
      await fetchAll();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }, [fetchAll]);

  const ignore = useCallback(async (id: number) => {
    setBusyId(id);
    try {
      await api.post(`/api/prompts/${id}/ignore`, {});
      await fetchAll();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }, [fetchAll]);

  if (prompts.length === 0 && (stats?.total_answered ?? 0) === 0) {
    // Nothing pending and nothing answered — don't take screen real
    // estate. The first time the server creates a prompt, the panel
    // will appear.
    return null;
  }

  return (
    <div className="panel">
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>
          Feedback
          {prompts.length > 0 && (
            <span style={{
              marginLeft: 8, fontSize: '0.85rem', fontWeight: 500, color: 'var(--accent)',
            }}>
              · {prompts.length} question{prompts.length === 1 ? '' : 's'}
            </span>
          )}
        </h3>
        <span className="muted" style={{ fontSize: '0.78rem' }}>
          You can Ignore any question — better than guessing.
        </span>
      </div>

      {error && (
        <div className="error" style={{ marginTop: 6, fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {prompts.length === 0 ? (
        <div className="muted" style={{ padding: '0.4rem 0', fontSize: '0.85rem' }}>
          No questions right now — they'll appear after some recordings finish analyzing.
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '0.5rem', marginTop: '0.4rem',
        }}>
          {prompts.map((p) => (
            <PromptCard
              key={p.id}
              prompt={p}
              busy={busyId === p.id}
              onAnswer={(response) => submit(p.id, response)}
              onIgnore={() => ignore(p.id)}
            />
          ))}
        </div>
      )}

      {stats && (
        <FeedbackStatsFooter stats={stats} selectedVehicle={selectedVehicle} />
      )}
    </div>
  );
}

// ---- Per-kind question rendering -------------------------------------

const KIND_TITLES: Record<FeedbackPromptKind, string> = {
  gauge_rpm:           'Confirm gauge RPM',
  driving_conditions:  'Driving context',
  anomaly_check:       'Did anything feel different?',
  repair_confirm:      'Did you do work on this vehicle?',
  speed_intensity:     'Approximate speed',
  powertrain_confirm:  'EV mode or engine mode?',
};

function PromptCard({
  prompt, busy, onAnswer, onIgnore,
}: {
  prompt: FeedbackPrompt;
  busy: boolean;
  onAnswer: (response: object) => void;
  onIgnore: () => void;
}) {
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '0.55rem 0.7rem',
      display: 'flex', flexDirection: 'column', gap: '0.4rem',
    }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
        {KIND_TITLES[prompt.kind]}
        {prompt.recording_id != null && ` · clip #${prompt.recording_id}`}
      </div>
      <PromptBody
        prompt={prompt}
        busy={busy}
        onAnswer={onAnswer}
      />
      <div style={{ marginTop: 'auto' }}>
        <button
          className="secondary"
          onClick={onIgnore}
          disabled={busy}
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.55rem' }}
          title="Skip — better than guessing if you're not sure"
        >
          Ignore
        </button>
      </div>
    </div>
  );
}

function PromptBody({
  prompt, busy, onAnswer,
}: {
  prompt: FeedbackPrompt;
  busy: boolean;
  onAnswer: (response: object) => void;
}) {
  switch (prompt.kind) {
    case 'gauge_rpm':           return <GaugeRpmBody prompt={prompt} busy={busy} onAnswer={onAnswer} />;
    case 'powertrain_confirm':  return <PowertrainBody prompt={prompt} busy={busy} onAnswer={onAnswer} />;
    case 'speed_intensity':     return <SpeedBody prompt={prompt} busy={busy} onAnswer={onAnswer} />;
    case 'driving_conditions':  return <ConditionsBody busy={busy} onAnswer={onAnswer} />;
    case 'anomaly_check':       return <AnomalyBody prompt={prompt} busy={busy} onAnswer={onAnswer} />;
    case 'repair_confirm':      return <RepairBody prompt={prompt} busy={busy} onAnswer={onAnswer} />;
  }
}

function GaugeRpmBody({ prompt, busy, onAnswer }: BodyProps) {
  const consensus = (prompt.context?.consensus_rpm as number | undefined);
  const agreement = prompt.context?.agreement as string | undefined;
  const [val, setVal] = useState('');
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>
        What did the dashboard tach read on this clip?
      </div>
      {consensus != null && (
        <div className="muted" style={{ fontSize: '0.78rem' }}>
          Our consensus: ~{Math.round(consensus)} RPM
          {agreement && ` (agreement: ${agreement})`}
        </div>
      )}
      <div className="row" style={{ marginTop: 4, gap: 6 }}>
        <input
          type="number" min={0} max={9999} step={50}
          value={val} onChange={(e) => setVal(e.target.value)}
          placeholder="e.g. 1800"
          disabled={busy}
          style={{ width: 120 }}
        />
        <button
          disabled={busy || val.trim() === ''}
          onClick={() => onAnswer({ gauge_rpm: Number(val) })}
        >Submit</button>
      </div>
    </div>
  );
}

function PowertrainBody({ prompt, busy, onAnswer }: BodyProps) {
  const predicted = prompt.context?.predicted_state as string | undefined;
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>
        Right now, was the engine running or were you in EV mode?
      </div>
      {predicted && (
        <div className="muted" style={{ fontSize: '0.78rem' }}>
          We classified this as: <strong>{predicted}</strong>
        </div>
      )}
      <div className="row" style={{ marginTop: 4, gap: 4, flexWrap: 'wrap' }}>
        <Chip onClick={() => onAnswer({ actual_state: 'engine' })} disabled={busy}>Engine</Chip>
        <Chip onClick={() => onAnswer({ actual_state: 'ev' })} disabled={busy}>EV mode</Chip>
        <Chip onClick={() => onAnswer({ actual_state: 'off' })} disabled={busy}>Engine off / coasting</Chip>
        <Chip onClick={() => onAnswer({ actual_state: 'unknown' })} disabled={busy} muted>
          Don't remember
        </Chip>
      </div>
    </div>
  );
}

function SpeedBody({ busy, onAnswer }: Omit<BodyProps, 'prompt'> & { prompt?: FeedbackPrompt }) {
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>
        Roughly what speed during this clip?
      </div>
      <div className="row" style={{ marginTop: 4, gap: 4, flexWrap: 'wrap' }}>
        <Chip onClick={() => onAnswer({ speed_band: 'stationary' })} disabled={busy}>Stationary</Chip>
        <Chip onClick={() => onAnswer({ speed_band: '<30' })} disabled={busy}>&lt; 30</Chip>
        <Chip onClick={() => onAnswer({ speed_band: '30-60' })} disabled={busy}>30–60</Chip>
        <Chip onClick={() => onAnswer({ speed_band: '60-100' })} disabled={busy}>60–100</Chip>
        <Chip onClick={() => onAnswer({ speed_band: '>100' })} disabled={busy}>&gt; 100</Chip>
        <Chip onClick={() => onAnswer({ speed_band: 'unknown' })} disabled={busy} muted>
          Don't remember
        </Chip>
      </div>
    </div>
  );
}

function ConditionsBody({ busy, onAnswer }: Omit<BodyProps, 'prompt'> & { prompt?: FeedbackPrompt }) {
  const [ac, setAc] = useState(false);
  const [windows, setWindows] = useState(false);
  const [rough, setRough] = useState(false);
  const [load, setLoad] = useState(false);
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>Anything notable about this drive?</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 4, fontSize: '0.85rem' }}>
        <label><input type="checkbox" checked={ac} onChange={(e) => setAc(e.target.checked)} disabled={busy} /> AC on</label>
        <label><input type="checkbox" checked={windows} onChange={(e) => setWindows(e.target.checked)} disabled={busy} /> Windows open</label>
        <label><input type="checkbox" checked={rough} onChange={(e) => setRough(e.target.checked)} disabled={busy} /> Rough road</label>
        <label><input type="checkbox" checked={load} onChange={(e) => setLoad(e.target.checked)} disabled={busy} /> Heavy load / towing</label>
      </div>
      <div className="row" style={{ marginTop: 4, gap: 4, flexWrap: 'wrap' }}>
        <button
          disabled={busy}
          onClick={() => onAnswer({ ac, windows, rough_road: rough, heavy_load: load })}
        >Submit</button>
        <Chip onClick={() => onAnswer({ ac: false, windows: false, rough_road: false, heavy_load: false, none: true })} disabled={busy} muted>
          None of these
        </Chip>
      </div>
    </div>
  );
}

function AnomalyBody({ prompt, busy, onAnswer }: BodyProps) {
  const cur = prompt.context?.current as number | undefined;
  const baseline = prompt.context?.baseline_mean as number | undefined;
  const [notes, setNotes] = useState('');
  const submit = (felt: 'yes' | 'no' | 'unknown') => {
    const response: Record<string, unknown> = { felt_different: felt };
    if (notes.trim() !== '') response.notes = notes.trim();
    onAnswer(response);
  };
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>
        Smoothness dropped this drive
        {cur != null && baseline != null
          ? ` (score ${Math.round(cur)} vs your usual ${Math.round(baseline)})`
          : ''}.
        Did anything feel different?
      </div>
      <input
        type="text"
        value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="optional — what did you notice?"
        disabled={busy}
        maxLength={200}
        style={{ width: '100%', marginTop: 4 }}
      />
      <div className="row" style={{ marginTop: 4, gap: 4, flexWrap: 'wrap' }}>
        <Chip onClick={() => submit('yes')} disabled={busy}>Yes</Chip>
        <Chip onClick={() => submit('no')} disabled={busy}>No</Chip>
        <Chip onClick={() => submit('unknown')} disabled={busy} muted>Don't remember</Chip>
      </div>
    </div>
  );
}

function RepairBody({ prompt, busy, onAnswer }: BodyProps) {
  const cur = prompt.context?.smoothness_current as number | undefined;
  const baseline = prompt.context?.smoothness_baseline as number | undefined;
  const [notes, setNotes] = useState('');
  const submit = (did: 'yes' | 'no' | 'unknown') => {
    const response: Record<string, unknown> = { did_repair: did };
    if (notes.trim() !== '') response.notes = notes.trim();
    onAnswer(response);
  };
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>
        Smoothness improved this drive
        {cur != null && baseline != null
          ? ` (score ${Math.round(cur)} vs your usual ${Math.round(baseline)})`
          : ''}.
        Did you have work done on the vehicle recently?
      </div>
      <input
        type="text"
        value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="optional — what was done?"
        disabled={busy}
        maxLength={200}
        style={{ width: '100%', marginTop: 4 }}
      />
      <div className="row" style={{ marginTop: 4, gap: 4, flexWrap: 'wrap' }}>
        <Chip onClick={() => submit('yes')} disabled={busy}>Yes</Chip>
        <Chip onClick={() => submit('no')} disabled={busy}>No</Chip>
        <Chip onClick={() => submit('unknown')} disabled={busy} muted>Don't remember</Chip>
      </div>
    </div>
  );
}

interface BodyProps {
  prompt: FeedbackPrompt;
  busy: boolean;
  onAnswer: (response: object) => void;
}

function Chip({
  children, onClick, disabled, muted,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  muted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={muted ? 'secondary' : undefined}
      style={{
        fontSize: '0.85rem', padding: '0.2rem 0.55rem',
        borderRadius: 999,
      }}
    >
      {children}
    </button>
  );
}

// ---- Visible-payoff footer ------------------------------------------

function FeedbackStatsFooter({
  stats, selectedVehicle,
}: { stats: FeedbackStats; selectedVehicle: Vehicle | null }) {
  const total = stats.total_answered;
  if (total === 0) {
    return (
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
        Once you start answering, totals appear here so you can see what your
        feedback is worth. Threshold tuning uses these as ground truth.
      </div>
    );
  }
  // Pick the top 3 kinds by answered count to show in the footer.
  const top = useMemo(
    () => Object.entries(stats.by_kind)
      .map(([k, v]) => ({ kind: k as FeedbackPromptKind, count: v.answered }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4),
    [stats],
  );
  return (
    <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
      <strong style={{ color: 'var(--fg)' }}>{total}</strong> ground-truth
      point{total === 1 ? '' : 's'} collected
      {selectedVehicle != null && ` for ${selectedVehicle.name}`}
      {top.length > 0 && ' · '}
      {top.map((r, i) => (
        <span key={r.kind}>
          {i > 0 && ', '}
          {KIND_TITLES[r.kind].toLowerCase()} ×{r.count}
        </span>
      ))}
      {stats.total_ignored > 0 && (
        <span> · {stats.total_ignored} ignored</span>
      )}
    </div>
  );
}
